function getDataTypeIconExpression(types = []) {
  const iconMatches = types.flatMap(type => [
    String(type.id),
    `nazka_${type.icon || 'question'}`,
  ])

  return [
    'match',
    ['to-string', ['get', 'dataType']],
    ...iconMatches,
    'nazka_question',
  ]
}

function getDataTypeColorExpression(types = []) {
  const colorMatches = types.flatMap(type => [
    String(type.id),
    type.color || '#ff0000',
  ])

  return [
    'match',
    ['to-string', ['get', 'dataType']],
    ...colorMatches,
    '#ff0000',
  ]
}

function getLabResultPieIconExpression() {
  return [
    'concat',
    'nazka_lab-result-pie-',
    ['to-string', [
      'round',
      [
        '*',
        [
          '/',
          ['get', 'positiveCount'],
          ['+', ['get', 'positiveCount'], ['get', 'negativeCount']],
        ],
        20,
      ],
    ]],
  ]
}

export default function getLayers(types = []) {
  return [
    {
      id: 'environment-polygon-layer',
      type: 'fill',
      source: 'environment',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.2,
      },
      filter: ['==', ['geometry-type'], 'Polygon'],
    },
    {
      id: 'breeding-sites-polygon-layer',
      type: 'fill',
      source: 'breeding-sites',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.2,
      },
    },
    {
      id: 'environment-line-layer',
      type: 'line',
      source: 'environment',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
      },
    },
    {
      id: 'breeding-sites-line-layer',
      type: 'line',
      source: 'breeding-sites',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
      },
    },
    {
      'id': 'breeding-sites-layer-osm',
      'type': 'symbol',
      'source': 'environment-osm',
      'source-layer': 'osm-data',
      'layout': {
        'icon-image': 'nazka_sdf-triangle',
        'icon-allow-overlap': true,
        'icon-size': 0.6,
        'visibility': 'none',
      },
      'paint': {
        'icon-color': getDataTypeColorExpression(types),
      },
      'filter': [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', 'source'], 'osm'],
        ['>', ['to-number', ['get', 'dataType']], 11],
      ],
    },
    {
      id: 'breeding-sites-layer',
      type: 'symbol',
      source: 'breeding-sites',
      layout: {
        'icon-image': 'nazka_sdf-triangle',
        'icon-allow-overlap': true,
        'icon-size': 0.6,
      },
      paint: {
        'icon-color': ['get', 'color'],
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      'id': 'environment-layer-osm',
      'type': 'symbol',
      'source': 'environment-osm',
      'source-layer': 'osm-data',
      'layout': {
        'icon-image': getDataTypeIconExpression(types),
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
        'icon-size': 0.25,
        'visibility': 'none',
      },
      'paint': {
        'icon-color': getDataTypeColorExpression(types),
      },
      'filter': [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', 'source'], 'osm'],
        ['<', ['to-number', ['get', 'dataType']], 12],
      ],
    },
    {
      id: 'environment-layer',
      type: 'symbol',
      source: 'environment',
      layout: {
        'icon-image': ['concat', 'nazka_', ['get', 'icon']],
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
        'icon-size': 0.25,
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      id: 'mosquito-observations-layer',
      type: 'circle',
      source: 'mosquito-observations',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 5,
      },
    },
    {
      id: 'lab-results-clusters-layer',
      type: 'symbol',
      source: 'lab-results',
      layout: {
        'icon-image': getLabResultPieIconExpression(),
        'icon-size': 0.35,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      filter: ['has', 'point_count'],
    },
    {
      id: 'lab-results-layer',
      type: 'symbol',
      source: 'lab-results',
      layout: {
        'icon-image': [
          'concat',
          'nazka_lab-result-pie-',
          ['to-string', ['get', 'pieBucket']],
        ],
        'icon-size': ['case', ['get', 'isMixed'], 0.35, 0.175],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      filter: ['!', ['has', 'point_count']],
    },
    {
      id: 'hovered-lab-results-layer',
      type: 'symbol',
      source: 'hovered-lab-results',
      layout: {
        'icon-image': 'nazka_hovered-specimen',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['get', 'analysisCount'],
          1, 0.3,
          5, 0.48,
          10, 0.72,
          25, 1.08,
        ],
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      id: 'trap-buffers-layer',
      type: 'line',
      source: 'trap-buffers',
      paint: {
        'line-color': '#000000',
        'line-width': 1,
        'line-opacity': 1,
      },
      layout: {
        'line-join': 'round',
      },
    },
    {
      id: 'trap-line-connections-layer',
      type: 'line',
      source: 'trap-connections',
      paint: {
        'line-color': '#000000',
        'line-width': 1,
        'line-opacity': 0.5,
        'line-dasharray': [3, 1],
      },
    },
    {
      id: 'trap-layer',
      type: 'symbol',
      source: 'traps',
      layout: {
        'icon-image': [
          'case',
          ['==', ['get', 'hasActive'], true], 'nazka_trap-cluster-pulsing',
          ['has', 'point_count'], 'nazka_trap-cluster',
          ['==', ['get', 'status'], 'active'], 'nazka_trap-pulsing',
          ['==', ['get', 'status'], 'passive'], 'nazka_trap-passive',
          ['==', ['get', 'status'], 'inactive'], 'nazka_trap-inactive',
          'nazka_trap-inactive',
        ],
        'icon-allow-overlap': true,
        'icon-offset': [
          'case',
          ['==', ['get', 'status'], 'active'], ['literal', [0, -1]],
          ['literal', [0, 0]],
        ],
        'text-field': ['get', 'point_count'],
        'text-size': 11,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-offset': [-0.05, 0.05],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#fff',
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      id: 'hovered-trap-layer',
      type: 'symbol',
      source: 'hovered-trap',
      layout: {
        'icon-image': 'nazka_hovered-trap',
        'icon-allow-overlap': true,
        'icon-size': 0.65,
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      id: 'planner-traps-layer',
      type: 'symbol',
      source: 'planner-traps',
      layout: {
        'icon-image': 'nazka_trap-strategy',
        'icon-allow-overlap': true,
      },
    },
    {
      id: 'trap-line-connections-labels',
      type: 'symbol',
      source: 'trap-connections',
      layout: {
        'symbol-placement': 'line-center',
        'text-field': ['get', 'length'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    },
    {
      id: 'hovered-mosquito-observations-layer',
      type: 'symbol',
      source: 'hovered-mosquito-observations',
      layout: {
        'icon-image': 'nazka_hovered-specimen',
        'icon-allow-overlap': true,
        'icon-size': 0.3,
      },
      filter: ['==', ['geometry-type'], 'Point'],
    },
    {
      id: 'alerts-layer-bg',
      type: 'symbol',
      source: 'alerts',
      layout: {
        'icon-image': 'nazka_triangle-exclamation-bg',
        'icon-allow-overlap': true,
        'icon-size': 0.5,
      },
    },
    {
      id: 'alerts-layer',
      type: 'symbol',
      source: 'alerts',
      layout: {
        'icon-image': 'nazka_sdf-triangle-exclamation',
        'icon-allow-overlap': true,
        'icon-size': 0.5,
      },
      paint: {
        'icon-color': ['get', 'color'],
      },
    },
    {
      id: 'hovered-alerts-layer',
      type: 'symbol',
      source: 'hovered-alert',
      layout: {
        'icon-image': 'nazka_hovered-alert',
        'icon-allow-overlap': true,
        'icon-size': 0.5,
        'icon-offset': [0, -2],
      },
    },
    {
      id: 'new-alert-layer-bg',
      type: 'symbol',
      source: 'new-alert',
      layout: {
        'icon-image': 'nazka_triangle-exclamation-bg',
        'icon-allow-overlap': true,
        'icon-size': 0.5,
      },
    },
    {
      id: 'new-alert-layer',
      type: 'symbol',
      source: 'new-alert',
      layout: {
        'icon-image': 'nazka_sdf-triangle-exclamation',
        'icon-allow-overlap': true,
        'icon-size': 0.5,
      },
      paint: {
        'icon-color': ['get', 'color'],
      },
    },
  ]
}
